import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserType } from "@/hooks/useUserType";

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  targetSelector: string | null; // CSS selector for element to highlight, null for center screen
  route: string | ((navigate: any) => Promise<string | null>); // Which route this step appears on, or function to get route dynamically
  position: "top" | "bottom" | "left" | "right" | "center";
  action?: "click" | "hover" | "none"; // What action the user should take
  waitForElement?: boolean; // Wait for element to appear before showing
  switchTab?: string; // Tab value to switch to (e.g., "materials" for materials tab)
  tabSelector?: string; // CSS selector for the tab trigger to click
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "welcome",
    title: "Welcome to Distilled Intuition! 🎉",
    description: "Let's take a quick tour of the key features that will help you create amazing learning experiences. This will only take a minute!",
    targetSelector: null,
    route: "/dashboard",
    position: "center",
  },
  {
    id: "create-workspace",
    title: "Create a Workspace",
    description: "Workspaces are containers for your courses and materials. Click here to create your first workspace. You can organize multiple courses and share materials across them.",
    targetSelector: "[data-tutorial='create-workspace']",
    route: "/dashboard",
    position: "bottom",
    waitForElement: true,
  },
  {
    id: "workspace-overview",
    title: "Inside a Workspace",
    description: "Each workspace has two main areas: Courses (where you build learning paths) and Materials Library (where you store documents for AI to use when generating content).",
    targetSelector: "[data-tutorial='workspace-tabs']",
    route: async () => {
      // Get first workspace or create one
      const { data: workspaces } = await supabase
        .from("workspaces")
        .select("id")
        .order("created_at", { ascending: true })
        .limit(1);
      
      if (workspaces && workspaces.length > 0) {
        return `/workspace/${workspaces[0].id}`;
      }
      
      // Create a default workspace
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Try direct insert first, fallback to RPC
        let newWorkspace: { id: string } | null = null;
        const direct = await supabase
          .from("workspaces")
          .insert({
            user_id: user.id,
            name: "My Workspace",
            description: "Default workspace",
          })
          .select("id")
          .single();

        if (direct.data) {
          newWorkspace = direct.data;
        } else {
          const rpc = await supabase.rpc("create_workspace", {
            p_name: "My Workspace",
            p_description: "Default workspace",
          });
          if (rpc.data) newWorkspace = rpc.data;
        }

        if (newWorkspace) {
          return `/workspace/${newWorkspace.id}`;
        }
      }
      return null;
    },
    position: "bottom",
    waitForElement: true,
  },
  {
    id: "create-course",
    title: "Create a Course",
    description: "Click here to create a new course. Our AI-powered chat will guide you through defining your learner profile, success measures, and learning objectives.",
    targetSelector: "[data-tutorial='create-course']",
    route: "/dashboard",
    position: "bottom",
    waitForElement: true,
  },
  {
    id: "upload-material",
    title: "Upload Materials",
    description: "Upload your existing content here - PDFs, documents, or URLs. The AI will use these materials to generate relevant course content tailored to your source material.",
    targetSelector: "[data-tutorial='upload-material']",
    route: async () => {
      const { data: workspaces } = await supabase
        .from("workspaces")
        .select("id")
        .order("created_at", { ascending: true })
        .limit(1);
      
      if (workspaces && workspaces.length > 0) {
        return `/workspace/${workspaces[0].id}`;
      }
      return null;
    },
    position: "bottom",
    waitForElement: true,
    switchTab: "materials", // Switch to materials tab
  },
  {
    id: "share-workspace",
    title: "Share with Collaborators",
    description: "Click Share to invite other users to your workspace or course. You can give them Editor (can modify) or Viewer (read-only) access.",
    targetSelector: "[data-tutorial='share-workspace']",
    route: async () => {
      const { data: workspaces } = await supabase
        .from("workspaces")
        .select("id")
        .order("created_at", { ascending: true })
        .limit(1);
      
      if (workspaces && workspaces.length > 0) {
        return `/workspace/${workspaces[0].id}`;
      }
      return null;
    },
    position: "bottom",
    waitForElement: true,
  },
  {
    id: "skill-tree-tab",
    title: "Generate a Skill Tree",
    description: "In your course, go to the Skill Tree tab and click 'Generate Skill Tree'. The AI will analyze your materials and objectives to create a visual learning path.",
    targetSelector: "[data-tutorial='skill-tree-tab']",
    route: async () => {
      // Get first course or create one
      const { data: courses } = await supabase
        .from("learning_projects")
        .select("id")
        .order("created_at", { ascending: true })
        .limit(1);
      
      if (courses && courses.length > 0) {
        return `/project/${courses[0].id}`;
      }
      return null;
    },
    position: "bottom",
    waitForElement: true,
  },
  {
    id: "edit-node",
    title: "Edit Skill Nodes",
    description: "Click on any node in the skill tree to edit it. You can change the title, description, and manage its content - lessons, exams, and slides.",
    targetSelector: "[data-tutorial='skill-node']",
    route: async () => {
      const { data: courses } = await supabase
        .from("learning_projects")
        .select("id")
        .order("created_at", { ascending: true })
        .limit(1);
      
      if (courses && courses.length > 0) {
        return `/project/${courses[0].id}`;
      }
      return null;
    },
    position: "right",
    waitForElement: true,
  },
  {
    id: "generate-content",
    title: "Generate Course Content",
    description: "For each node, you can use AI to automatically generate lessons, quizzes, and presentation slides based on your uploaded materials and objectives.",
    targetSelector: "[data-tutorial='generate-content']",
    route: async () => {
      const { data: courses } = await supabase
        .from("learning_projects")
        .select("id")
        .order("created_at", { ascending: true })
        .limit(1);
      
      if (courses && courses.length > 0) {
        return `/project/${courses[0].id}`;
      }
      return null;
    },
    position: "left",
    waitForElement: true,
  },
  {
    id: "preview-learner",
    title: "Preview as Learner",
    description: "Click 'Preview as Learner' to see exactly what your students will experience. Test the flow, quizzes, and content before publishing.",
    targetSelector: "[data-tutorial='preview-learner']",
    route: async () => {
      const { data: courses } = await supabase
        .from("learning_projects")
        .select("id")
        .order("created_at", { ascending: true })
        .limit(1);
      
      if (courses && courses.length > 0) {
        return `/project/${courses[0].id}`;
      }
      return null;
    },
    position: "bottom",
    waitForElement: true,
  },
  {
    id: "complete",
    title: "You're All Set! 🚀",
    description: "That's it! You now know the basics. Start by creating a workspace and uploading your course materials. The AI will help you build engaging learning experiences.",
    targetSelector: null,
    route: "/dashboard",
    position: "center",
  },
];

interface SpotlightOverlayProps {
  targetRect: DOMRect | null;
  children: React.ReactNode;
}

const SpotlightOverlay = ({ targetRect, children }: SpotlightOverlayProps) => {
  const padding = 8;
  
  if (!targetRect) {
    // Center modal, no spotlight
    return (
      <div className="fixed inset-0 z-[9999]">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      </div>
    );
  }

  const spotlightStyle = {
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Dark overlay with cutout */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={spotlightStyle.left}
              y={spotlightStyle.top}
              width={spotlightStyle.width}
              height={spotlightStyle.height}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
        />
      </svg>
      
      {/* Highlight ring around target */}
      <div
        className="absolute border-2 border-primary rounded-lg pointer-events-none animate-pulse"
        style={{
          top: spotlightStyle.top,
          left: spotlightStyle.left,
          width: spotlightStyle.width,
          height: spotlightStyle.height,
        }}
      />
      
      {/* Content */}
      {children}
    </div>
  );
};

interface TutorialCardProps {
  step: TutorialStep;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  targetRect: DOMRect | null;
}

const TutorialCard = ({
  step,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  targetRect,
}: TutorialCardProps) => {
  const getCardPosition = (): React.CSSProperties => {
    if (!targetRect || step.position === "center") {
      return {};
    }

    const cardWidth = 400;
    const cardHeight = 200;
    const margin = 16;

    switch (step.position) {
      case "top":
        return {
          position: "absolute",
          top: Math.max(margin, targetRect.top - cardHeight - margin),
          left: Math.max(margin, targetRect.left + targetRect.width / 2 - cardWidth / 2),
        };
      case "bottom":
        return {
          position: "absolute",
          top: targetRect.bottom + margin,
          left: Math.max(margin, Math.min(window.innerWidth - cardWidth - margin, targetRect.left + targetRect.width / 2 - cardWidth / 2)),
        };
      case "left":
        return {
          position: "absolute",
          top: Math.max(margin, targetRect.top + targetRect.height / 2 - cardHeight / 2),
          left: Math.max(margin, targetRect.left - cardWidth - margin),
        };
      case "right":
        return {
          position: "absolute",
          top: Math.max(margin, targetRect.top + targetRect.height / 2 - cardHeight / 2),
          left: Math.min(window.innerWidth - cardWidth - margin, targetRect.right + margin),
        };
      default:
        return {};
    }
  };

  return (
    <Card
      className="w-[400px] max-w-[90vw] shadow-2xl border-primary/20 bg-background"
      style={getCardPosition()}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{step.title}</CardTitle>
          </div>
          <Button variant="ghost" size="icon" onClick={onSkip} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">{step.description}</p>
      </CardContent>
      <CardFooter className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-1">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentIndex ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          {currentIndex > 0 && (
            <Button variant="outline" size="sm" onClick={onPrev}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <Button size="sm" onClick={onNext}>
            {currentIndex === totalSteps - 1 ? (
              "Finish"
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default function OnboardingTutorial() {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [availableSteps, setAvailableSteps] = useState<TutorialStep[]>(TUTORIAL_STEPS);
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const { userType } = useUserType(session?.user?.id);

  const currentStep = availableSteps[currentStepIndex] || availableSteps[0];

  // Don't run tutorial for "other" users (one free course flow)
  const isOtherUser = userType === "other_no_course" || userType === "other_one_course";

  // Check if user has completed tutorial and filter steps based on available resources
  useEffect(() => {
    if (isOtherUser) {
      setHasCheckedStatus(true);
      return;
    }
    const checkTutorialStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("tutorial_completed")
        .eq("id", user.id)
        .single();

      if (profile && !profile.tutorial_completed) {
        // Filter steps based on available resources
        const filteredSteps: TutorialStep[] = [];
        
        // Check if user has courses (needed for course-related steps)
        const { data: courses } = await supabase
          .from("learning_projects")
          .select("id")
          .limit(1);
        
        const hasCourses = courses && courses.length > 0;
        
        // Filter steps - include course-related steps only if courses exist
        for (const step of TUTORIAL_STEPS) {
          const requiresCourse = ['skill-tree-tab', 'edit-node', 'generate-content', 'preview-learner'].includes(step.id);
          
          if (!requiresCourse || hasCourses) {
            filteredSteps.push(step);
          }
        }
        
        setAvailableSteps(filteredSteps);
        
        // Small delay to let the page render first
        setTimeout(() => {
          setIsActive(true);
        }, 500);
      }
      setHasCheckedStatus(true);
    };

    checkTutorialStatus();
  }, [isOtherUser]);

  // Update target element rect when step changes
  const updateTargetRect = useCallback(() => {
    if (!currentStep.targetSelector) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(currentStep.targetSelector);
    if (element) {
      setTargetRect(element.getBoundingClientRect());
    } else if (currentStep.waitForElement) {
      // Element not found yet, wait and retry
      const timer = setTimeout(updateTargetRect, 100);
      return () => clearTimeout(timer);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    if (!isActive || !isReady || isNavigating) return;
    
    updateTargetRect();
    
    // Update on resize
    window.addEventListener("resize", updateTargetRect);
    window.addEventListener("scroll", updateTargetRect);
    
    return () => {
      window.removeEventListener("resize", updateTargetRect);
      window.removeEventListener("scroll", updateTargetRect);
    };
  }, [isActive, currentStepIndex, isReady, isNavigating, updateTargetRect]);

  // Navigate to correct route for step if needed
  useEffect(() => {
    if (!isActive) return;
    
    const navigateToStepRoute = async () => {
      setIsNavigating(true);
      setIsReady(false);
      
      const stepRoute = currentStep.route;
      let targetRoute: string | null = null;
      
      // Handle dynamic routes (functions)
      if (typeof stepRoute === "function") {
        targetRoute = await stepRoute(navigate);
      } else {
        targetRoute = stepRoute;
      }
      
      // Check if we need to navigate
      const currentPath = location.pathname;
      
      // Check if we're on a matching route pattern
      const isMatchingRoute = targetRoute && (
        currentPath === targetRoute ||
        (targetRoute.startsWith("/workspace/") && currentPath.startsWith("/workspace/")) ||
        (targetRoute.startsWith("/project/") && currentPath.startsWith("/project/"))
      );
      
      if (!isMatchingRoute && targetRoute) {
        // Navigate to target route
        navigate(targetRoute);
        
        // Wait for navigation to complete
        // React Router navigation is async, give it time
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Additional wait if we still don't match (handles slow page loads)
        let retries = 20;
        while (retries > 0) {
          const newPath = window.location.pathname;
          const matches = newPath === targetRoute || 
            (targetRoute.startsWith("/workspace/") && newPath.startsWith("/workspace/")) || 
            (targetRoute.startsWith("/project/") && newPath.startsWith("/project/"));
          
          if (matches) break;
          
          await new Promise(resolve => setTimeout(resolve, 100));
          retries--;
        }
      }
      
      // Give page a moment to render after navigation
      // Wait longer to ensure React components are fully mounted and interactive
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Switch tabs if needed
      if (currentStep.switchTab) {
        // Wait for tabs to be fully rendered and interactive
        // Check if tabs are actually in the DOM
        let tabListRetries = 20;
        while (tabListRetries > 0) {
          const tabList = document.querySelector('[data-tutorial="workspace-tabs"]');
          if (tabList) {
            // Tabs are rendered, wait a bit more for React to finish
            await new Promise(resolve => setTimeout(resolve, 300));
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 100));
          tabListRetries--;
        }
        
        // Try finding tab trigger by data attribute first (most reliable)
        let tabTrigger = document.querySelector(`[data-tutorial-tab="${currentStep.switchTab}"]`) as HTMLElement;
        
        // Fallback: find by custom tabSelector if provided
        if (!tabTrigger && currentStep.tabSelector) {
          tabTrigger = document.querySelector(currentStep.tabSelector) as HTMLElement;
        }
        
        // Fallback: find by text content within workspace tabs
        if (!tabTrigger) {
          const tabList = document.querySelector('[data-tutorial="workspace-tabs"]');
          if (tabList) {
            const allButtons = Array.from(tabList.querySelectorAll('button'));
            tabTrigger = allButtons.find(btn => {
              const text = btn.textContent || '';
              if (currentStep.switchTab === 'materials') {
                return text.includes('Materials');
              }
              return false;
            }) as HTMLElement || null;
          }
        }
        
        if (tabTrigger) {
          // Scroll into view if needed
          tabTrigger.scrollIntoView({ behavior: 'smooth', block: 'center' });
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Focus the tab trigger first
          tabTrigger.focus();
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // Click the tab trigger using multiple methods for reliability
          // Method 1: MouseEvent
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            buttons: 1,
          });
          tabTrigger.dispatchEvent(clickEvent);
          
          // Method 2: Direct click
          tabTrigger.click();
          
          // Method 3: mousedown + mouseup + click
          const mouseDownEvent = new MouseEvent('mousedown', { bubbles: true, cancelable: true });
          const mouseUpEvent = new MouseEvent('mouseup', { bubbles: true, cancelable: true });
          tabTrigger.dispatchEvent(mouseDownEvent);
          await new Promise(resolve => setTimeout(resolve, 50));
          tabTrigger.dispatchEvent(mouseUpEvent);
          tabTrigger.dispatchEvent(clickEvent);
          
          // Wait for tab content to be visible
          await new Promise(resolve => setTimeout(resolve, 400));
          
          // Verify tab content is visible by checking for the tab content element
          let retries = 30;
          while (retries > 0) {
            // Check if tab content is visible
            const tabContent = document.querySelector(`[data-tutorial-tab-content="${currentStep.switchTab}"]`);
            const isContentVisible = tabContent && (tabContent as HTMLElement).offsetParent !== null;
            
            // Also check if target element is visible
            if (currentStep.targetSelector) {
              const targetElement = document.querySelector(currentStep.targetSelector);
              const isTargetVisible = targetElement && (targetElement as HTMLElement).offsetParent !== null;
              
              if (isContentVisible && isTargetVisible) {
                // Tab switched and target is visible
                break;
              }
            } else if (isContentVisible) {
              // Tab switched (no target to check)
              break;
            }
            
            // If still not visible, try clicking again every few retries
            if (retries % 5 === 0) {
              tabTrigger.click();
              tabTrigger.dispatchEvent(clickEvent);
              await new Promise(resolve => setTimeout(resolve, 200));
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            retries--;
          }
        } else {
          console.warn(`Could not find tab trigger for: ${currentStep.switchTab}`);
        }
      }
      
      // Wait for page to render and element to be available (if needed)
      if (currentStep.waitForElement && currentStep.targetSelector) {
        let retries = 50; // 5 seconds max wait
        while (retries > 0) {
          const element = document.querySelector(currentStep.targetSelector);
          if (element && (element as HTMLElement).offsetParent !== null) {
            // Element exists and is visible (not hidden by display:none)
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 100));
          retries--;
        }
      } else {
        // Give page a moment to render
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setIsNavigating(false);
      setIsReady(true);
    };
    
    navigateToStepRoute();
  }, [isActive, currentStepIndex, currentStep, navigate, location.pathname]);

  const completeTutorial = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from("profiles")
        .update({ tutorial_completed: true })
        .eq("id", user.id);
    }
    setIsActive(false);
  };

  const handleNext = () => {
    if (currentStepIndex < availableSteps.length - 1) {
      setIsReady(false);
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      completeTutorial();
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setIsReady(false);
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleSkip = () => {
    completeTutorial();
  };

  if (session && isOtherUser) return null;
  if (!isActive || !hasCheckedStatus || isNavigating || !isReady) return null;

  return createPortal(
    <SpotlightOverlay targetRect={targetRect}>
      <TutorialCard
        step={currentStep}
        currentIndex={currentStepIndex}
        totalSteps={availableSteps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        targetRect={targetRect}
      />
    </SpotlightOverlay>,
    document.body
  );
}

// Re-export useTutorial for backwards compatibility (prefer importing from @/hooks/useTutorial)
export { useTutorial } from "@/hooks/useTutorial";

